import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * Comparison slider component for comparing two images
 *
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} mediaWrapper - The container for the images
 * @property {HTMLInputElement} slider - The range input element
 * @property {HTMLElement} afterImage - The image that gets revealed
 *
 * @extends {Component<ComparisonSliderRefs>}
 *
 * @property {string[]} requiredRefs - Required refs: 'mediaWrapper', 'slider', and 'afterImage'
 */
/** Intro hint: single raised-cosine glide out from rest and back. */
const HINT_DELAY = 220;
const HINT_DURATION = 900;
const HINT_AMPLITUDE = 8;

export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage'];

  constructor() {
    super();
    this.hasAnimated = false;
    this.isAnimating = false;
    this.hintDelayTimer = null;
    this.hintFrame = null;
    this.boundHandleIntersection = this.handleIntersection.bind(this);
  }

  /**
   * Called when component is added to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper } = this.refs;

    // Get orientation from media wrapper
    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';

    // Initialize the position
    this.sync();

    // Set up intersection observer for animation
    this.setupIntersectionObserver();
  }

  /**
   * Sync the CSS custom property with the input value.
   * Bound to the range input's `input` and `pointerdown` events, so a call here
   * means the user has taken over — the intro hint is cancelled for good.
   */
  sync() {
    const { mediaWrapper } = this.refs;

    if (this.isAnimating || this.hintDelayTimer) this.cancelHint();

    mediaWrapper.style.setProperty('--compare', String(Math.round(this.sliderPercent())));
  }

  /**
   * Current slider position as a 0-100 percentage
   * @returns {number}
   */
  sliderPercent() {
    const { slider } = this.refs;
    const min = Number(slider.min);
    const range = Number(slider.max) - min;
    if (!range) return 50;

    return ((Number(slider.value) - min) / range) * 100;
  }

  /**
   * Clean up when component is removed
   */
  disconnectedCallback() {
    this.cancelHint();

    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Set the slider value and update display
   * @param {number} value - Value between 0-100 (0 = all after, 100 = all before)
   */
  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  /**
   * Hint that the divider is draggable: one continuous glide out from the resting
   * position and back, driven frame-by-frame so the motion never depends on a CSS
   * transition being declared. Runs at most once, and the slider's own value is
   * never touched — the sweep returns to exactly where it started.
   */
  animateSlider() {
    if (this.hasAnimated) return;

    const { mediaWrapper } = this.refs;
    const base = this.sliderPercent();
    if (!Number.isFinite(base)) return;

    this.hasAnimated = true;
    this.isAnimating = true;

    // The per-frame writes are the animation; a CSS transition would only smear them.
    mediaWrapper.style.setProperty('--transition-duration', '0s');

    const start = performance.now();

    const step = (now) => {
      const t = Math.min((now - start) / HINT_DURATION, 1);

      if (t < 1) {
        // Raised cosine: velocity is zero at the start, at the turn, and at the end,
        // so the out-and-back reads as one smooth glide rather than two moves.
        const offset = (HINT_AMPLITUDE * (1 - Math.cos(2 * Math.PI * t))) / 2;
        mediaWrapper.style.setProperty('--compare', (base + offset).toFixed(2));
        this.hintFrame = requestAnimationFrame(step);
        return;
      }

      mediaWrapper.style.setProperty('--compare', String(Math.round(base)));
      this.hintFrame = null;
      this.isAnimating = false;
      mediaWrapper.style.removeProperty('--transition-duration');
    };

    this.hintFrame = requestAnimationFrame(step);
  }

  /**
   * Stop the intro hint and leave the divider at the slider's real position.
   * Permanent — `hasAnimated` is never reset, so the hint cannot run again.
   */
  cancelHint() {
    this.hasAnimated = true;
    this.isAnimating = false;

    if (this.hintDelayTimer) {
      clearTimeout(this.hintDelayTimer);
      this.hintDelayTimer = null;
    }

    if (this.hintFrame) {
      cancelAnimationFrame(this.hintFrame);
      this.hintFrame = null;
    }

    this.refs.mediaWrapper?.style.removeProperty('--transition-duration');
  }

  /**
   * Set up intersection observer to detect when section comes into view
   */
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    // Reduced motion: the divider stays put, no intro hint at all.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.hasAnimated = true;
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Trigger when 50% of the component is visible
    };

    this.intersectionObserver = new IntersectionObserver(this.boundHandleIntersection, options);
    this.intersectionObserver.observe(this);
  }

  /**
   * Handle intersection observer callback
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        // Small delay so the images have settled before the hint plays
        this.hintDelayTimer = setTimeout(() => {
          this.hintDelayTimer = null;
          oncePerEditorSession(this, `comparison-slider-animated`, () => {
            this.animateSlider();
          });
        }, HINT_DELAY);

        // Disconnect observer after first animation
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    });
  }
}

// Register the custom element
if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}
